import React from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import './Medcall2PersonAssignmentForm.css';

export const MEDCALL2_NURSE_TITLES = ['LVN', 'MA', 'CNA', 'RN', 'LPT', 'NP', 'EMT'] as const;
export const MEDCALL2_PSYCHIATRIST_TITLES = ['MD', 'NP', 'PA'] as const;

const FIRSTNAME_REQUIRED = 'Firstname is required!';
const LASTNAME_REQUIRED = 'Lastname is required!';
const TITLE_REQUIRED = 'Title is required!';
const EMAIL_REQUIRED = 'Email is required!';
const EMAIL_INVALID = 'Please use validated email address!';
const EMAIL_DOMAIN_BLOCKED =
  'Please do not use personal email addresses. It must be an email address from your agency!';
const EMAIL_CONFIRM_REQUIRED = 'Please retype your email address!';
const EMAIL_CONFIRM_MISMATCH = 'The two emails that you entered do not match!';
const LEGACY_EMAIL_PATTERN = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;

export type Medcall2PersonAssignmentKind = 'nurse' | 'psychiatrist';

export type Medcall2PersonAssignmentDraft = {
  confirmEmail: string;
  email: string;
  firstname: string;
  lastname: string;
  title: string;
};

export type Medcall2PersonAssignmentSubmission = Omit<
  Medcall2PersonAssignmentDraft,
  'confirmEmail'
>;

export type Medcall2PersonAssignmentValidationErrors = Partial<
  Record<keyof Medcall2PersonAssignmentDraft, string>
>;

export type Medcall2PersonAssignmentFormProps = {
  disabled?: boolean;
  draft: Medcall2PersonAssignmentDraft;
  filterDomains: readonly string[];
  kind: Medcall2PersonAssignmentKind;
  onCancel: () => void;
  onDraftChange: (draft: Medcall2PersonAssignmentDraft) => void;
  onSubmittingChange?: (pending: boolean) => void;
  onValidSubmit: (
    submission: Medcall2PersonAssignmentSubmission,
  ) => Promise<void> | void;
};

const getEmailError = (email: string, filterDomains: readonly string[]) => {
  if (email === '') {
    return undefined;
  }

  if (!LEGACY_EMAIL_PATTERN.test(email)) {
    return EMAIL_INVALID;
  }

  const emailDomain = email.split('@')[1];
  if (filterDomains.includes(emailDomain)) {
    return EMAIL_DOMAIN_BLOCKED;
  }

  return undefined;
};

export const validateMedcall2PersonAssignmentDraft = (
  kind: Medcall2PersonAssignmentKind,
  draft: Medcall2PersonAssignmentDraft,
  filterDomains: readonly string[],
): Medcall2PersonAssignmentValidationErrors => {
  const errors: Medcall2PersonAssignmentValidationErrors = {};

  if (draft.firstname === '') {
    errors.firstname = FIRSTNAME_REQUIRED;
  }

  if (draft.lastname === '') {
    errors.lastname = LASTNAME_REQUIRED;
  }

  if (draft.title === '') {
    errors.title = TITLE_REQUIRED;
  }

  if (kind === 'nurse' && draft.email === '') {
    errors.email = EMAIL_REQUIRED;
  }

  const emailError = getEmailError(draft.email, filterDomains);
  if (emailError) {
    errors.email = emailError;
  }

  const confirmEmailRequired = kind === 'nurse' || draft.email !== '';
  if (confirmEmailRequired && draft.confirmEmail === '') {
    errors.confirmEmail = EMAIL_CONFIRM_REQUIRED;
  }

  if (draft.confirmEmail !== '' && draft.confirmEmail !== draft.email) {
    errors.confirmEmail = EMAIL_CONFIRM_MISMATCH;
  }

  return errors;
};

type AssignmentFieldProps = {
  children: React.ReactNode;
  error?: string;
  fieldId: string;
  label: string;
  required: boolean;
};

const AssignmentField = ({
  children,
  error,
  fieldId,
  label,
  required,
}: AssignmentFieldProps) => {
  const errorId = `${fieldId}-error`;
  const labelId = `${fieldId}-label`;

  return (
    <div
      className={`medcall2-person-assignment-form__field${error ? ' has-error' : ''}`}
      data-testid={`${fieldId}-field`}
    >
      <label
        className="medcall2-person-assignment-form__label"
        htmlFor={fieldId}
        id={labelId}
      >
        {required ? (
          <span
            aria-hidden="true"
            className="medcall2-person-assignment-form__required"
            data-testid={`${fieldId}-required`}
          >
            *
          </span>
        ) : null}
        <span>{label}</span>
        <span aria-hidden="true" className="medcall2-person-assignment-form__colon">:</span>
      </label>
      <div className="medcall2-person-assignment-form__control">
        <div className="medcall2-person-assignment-form__control-input">
          {children}
        </div>
        {error ? (
          <div
            className="medcall2-person-assignment-form__error"
            id={errorId}
            role="alert"
          >
            {error}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export const Medcall2PersonAssignmentForm = ({
  disabled = false,
  draft,
  filterDomains,
  kind,
  onCancel,
  onDraftChange,
  onSubmittingChange,
  onValidSubmit,
}: Medcall2PersonAssignmentFormProps) => {
  const {
    control,
    formState: { errors, isSubmitting },
    getValues,
    handleSubmit,
    register,
    setValue,
  } = useForm<Medcall2PersonAssignmentDraft>({
    defaultValues: draft,
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: true,
  });

  React.useEffect(() => {
    onSubmittingChange?.(isSubmitting);
    return () => onSubmittingChange?.(false);
  }, [isSubmitting, onSubmittingChange]);

  React.useEffect(() => {
    setValue('confirmEmail', draft.confirmEmail);
    setValue('email', draft.email);
    setValue('firstname', draft.firstname);
    setValue('lastname', draft.lastname);
    setValue('title', draft.title);
  }, [
    draft.confirmEmail,
    draft.email,
    draft.firstname,
    draft.lastname,
    draft.title,
    setValue,
  ]);

  const validateField = React.useCallback(
    (field: keyof Medcall2PersonAssignmentDraft, value: string) => {
      const candidate = {
        ...getValues(),
        [field]: value,
      };
      return validateMedcall2PersonAssignmentDraft(kind, candidate, filterDomains)[field] || true;
    },
    [filterDomains, getValues, kind],
  );

  const updateDraft = React.useCallback(
    (field: keyof Medcall2PersonAssignmentDraft, value: string) => {
      onDraftChange({
        ...draft,
        [field]: value,
      });
    },
    [draft, onDraftChange],
  );

  const firstnameField = register('firstname', {
    validate: (value) => validateField('firstname', value),
  });
  const lastnameField = register('lastname', {
    validate: (value) => validateField('lastname', value),
  });
  const emailField = register('email', {
    validate: (value) => validateField('email', value),
  });
  const confirmEmailField = register('confirmEmail', {
    validate: (value) => validateField('confirmEmail', value),
  });

  const formId = `medcall2-${kind}-assignment`;
  const firstnameId = `${formId}-firstname`;
  const lastnameId = `${formId}-lastname`;
  const titleId = `${formId}-title`;
  const emailId = `${formId}-email`;
  const confirmEmailId = `${formId}-confirm-email`;
  const titles = kind === 'nurse' ? MEDCALL2_NURSE_TITLES : MEDCALL2_PSYCHIATRIST_TITLES;
  const confirmEmailRequired = kind === 'nurse' || draft.email !== '';
  const controlsDisabled = disabled || isSubmitting;

  const submitValidatedDraft = React.useCallback(
    (values: Medcall2PersonAssignmentDraft) => onValidSubmit({
      email: values.email,
      firstname: values.firstname,
      lastname: values.lastname,
      title: values.title,
    }),
    [onValidSubmit],
  );

  return (
    <form
      aria-busy={isSubmitting}
      className="medcall2-person-assignment-form"
      data-kind={kind}
      data-testid={formId}
      noValidate
      onSubmit={handleSubmit(submitValidatedDraft)}
    >
      <div className="medcall2-person-assignment-form__body">
        <AssignmentField
          error={errors.firstname?.message}
          fieldId={firstnameId}
          label="Firstname"
          required
        >
          <Input
            {...firstnameField}
            aria-describedby={errors.firstname ? `${firstnameId}-error` : undefined}
            aria-invalid={Boolean(errors.firstname)}
            aria-required="true"
            disabled={controlsDisabled}
            id={firstnameId}
            onChange={(event) => {
              firstnameField.onChange(event);
              updateDraft('firstname', event.target.value);
            }}
            type="text"
            value={draft.firstname}
          />
        </AssignmentField>

        <AssignmentField
          error={errors.lastname?.message}
          fieldId={lastnameId}
          label="Lastname"
          required
        >
          <Input
            {...lastnameField}
            aria-describedby={errors.lastname ? `${lastnameId}-error` : undefined}
            aria-invalid={Boolean(errors.lastname)}
            aria-required="true"
            disabled={controlsDisabled}
            id={lastnameId}
            onChange={(event) => {
              lastnameField.onChange(event);
              updateDraft('lastname', event.target.value);
            }}
            type="text"
            value={draft.lastname}
          />
        </AssignmentField>

        <AssignmentField
          error={errors.title?.message}
          fieldId={titleId}
          label="Title"
          required
        >
          <Controller
            control={control}
            name="title"
            render={({ field }) => (
              <Select
                disabled={controlsDisabled}
                onValueChange={(value) => {
                  field.onChange(value);
                  updateDraft('title', value);
                }}
                value={draft.title || undefined}
              >
                <SelectTrigger
                  aria-describedby={errors.title ? `${titleId}-error` : undefined}
                  aria-invalid={Boolean(errors.title)}
                  aria-labelledby={`${titleId}-label`}
                  aria-required="true"
                  id={titleId}
                  onBlur={field.onBlur}
                  ref={field.ref}
                >
                  <SelectValue placeholder="Select Title" />
                </SelectTrigger>
                <SelectContent className="medcall2-person-assignment-form__select-content">
                  {titles.map((title) => (
                    <SelectItem
                      className="medcall2-person-assignment-form__select-item"
                      key={`${kind}-title-${title}`}
                      value={title}
                    >
                      {title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            rules={{
              validate: (value) => validateField('title', value),
            }}
          />
        </AssignmentField>

        <AssignmentField
          error={errors.email?.message}
          fieldId={emailId}
          label="Email"
          required={kind === 'nurse'}
        >
          <Input
            {...emailField}
            aria-describedby={errors.email ? `${emailId}-error` : undefined}
            aria-invalid={Boolean(errors.email)}
            aria-required={kind === 'nurse'}
            disabled={controlsDisabled}
            id={emailId}
            onChange={(event) => {
              emailField.onChange(event);
              updateDraft('email', event.target.value);
            }}
            type="text"
            value={draft.email}
          />
        </AssignmentField>

        <AssignmentField
          error={errors.confirmEmail?.message}
          fieldId={confirmEmailId}
          label="Retype Email"
          required={confirmEmailRequired}
        >
          <Input
            {...confirmEmailField}
            aria-describedby={errors.confirmEmail ? `${confirmEmailId}-error` : undefined}
            aria-invalid={Boolean(errors.confirmEmail)}
            aria-required={confirmEmailRequired}
            disabled={controlsDisabled}
            id={confirmEmailId}
            onChange={(event) => {
              confirmEmailField.onChange(event);
              updateDraft('confirmEmail', event.target.value);
            }}
            type="text"
            value={draft.confirmEmail}
          />
        </AssignmentField>

        <div className="medcall2-person-assignment-form__footer">
          <Button
            className="medcall2-person-assignment-form__cancel"
            disabled={controlsDisabled}
            id={`${formId}-cancel`}
            onClick={onCancel}
            type="button"
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            className="medcall2-person-assignment-form__submit"
            disabled={controlsDisabled}
            id={`${formId}-submit`}
            type="submit"
          >
            Submit
          </Button>
        </div>
      </div>
    </form>
  );
};
